import { useState } from "react";
import { useEffect } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { Entity, EntityCollection, EntityMetadataResponse, PaginatedEntityResponse } from "../../api/OasisBackendApi";
import IndexRequestManager from "./IndexRequestManager";
import EntityUploader from "./EntityUploader";
import MetadataModal from "./entityInjestion/MetadataModal";
import { IconSettings, IconUpload, IconDatabase, IconList, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { toast, ToastContainer } from "react-toastify";
import AddQueryToEntity from "./AddQueryToEntity";

const EntityCollectionDetail = ({databaseId}: {databaseId: string}) => {
    const [entityCollection, setEntityCollection] = useState<EntityCollection | null>(null);
    const [paginatedEntities, setPaginatedEntities] = useState<PaginatedEntityResponse | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showMetadataModal, setShowMetadataModal] = useState(false);
    const db = useOasisBackend();
    
    const fetchAllEntities = async (page: number = 1, size: number = 10) => {
        const response = await db.endpoints.api.apiDatabaseEntityCollectionAllEntitiesRetrieve(databaseId, {
            page: page,
            page_size: size
        });
        setPaginatedEntities(response.data);
    }
    const fetchEntityCollection = async () => {
        const response = await db.endpoints.api.apiDatabaseEntityCollectionRetrieve(databaseId);
        setEntityCollection(response.data);
    }

    useEffect(() => {
        fetchEntityCollection();
    }, [databaseId]);

    useEffect(() => {
        fetchAllEntities(currentPage, pageSize);
    }, [currentPage, pageSize]);

    if (!entityCollection) {
        return (
            <div className="flex items-center justify-center p-4 sm:p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-400 mx-auto mb-3 sm:mb-4"></div>
                    <p className="text-gray-300 text-sm sm:text-base">Loading entity collection...</p>
                </div>
            </div>
        )
    }

    const saveMetadata = async (metadata: EntityMetadataResponse) => {
        const response = await db.endpoints.api.apiDatabaseUpdateEntityCollectionMetadataCreate(databaseId, {
            entity_collection_metadata: {
                entity_type: metadata.entity_type,
                entity_description: metadata.entity_description,
                all_keys: metadata.all_keys,
                semantic_index_keys: metadata.semantic_index_keys,
                oasis_index_keys: metadata.oasis_index_keys,
                oasis_form_data_index_keys: metadata.oasis_form_data_index_keys,
                ui_mapping: metadata.ui_mapping,
                simple_keys: metadata.simple_keys
            }
        });
        setEntityCollection(response.data);
        if (response.status === 200) {
            setShowMetadataModal(false);
            toast.success("Metadata saved successfully");
        }
    }

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-8">
            {/* Header Section */}
            <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <div className="mb-3 sm:mb-0">
                        <h1 className="text-xl sm:text-2xl font-bold text-white">
                            Entity Collection
                        </h1>
                        <p className="text-gray-300 mt-1 text-sm sm:text-base">
                            Manage entities and configuration for this collection
                        </p>
                    </div>
                    {entityCollection.all_keys && (
                        <button 
                            onClick={() => {
                                setShowMetadataModal(true);
                            }}
                            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
                            title="Configure metadata"
                        >
                            <IconSettings size={16} className="sm:w-[18px] sm:h-[18px]" />
                            Configure Metadata
                        </button>
                    )}
                </div>
                
                {/* Collection Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
                    <div className="bg-gray-700 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <IconDatabase size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                            <span className="font-medium text-white text-sm sm:text-base">Collection ID</span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-300 font-mono break-all">{entityCollection.id}</p>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <IconSettings size={18} className="sm:w-5 sm:h-5 text-purple-400" />
                            <span className="font-medium text-white text-sm sm:text-base">Keys</span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-300">{entityCollection.all_keys?.length || 0} keys configured</p>
                    </div>
                </div>
            </div>

            {/* Upload Section */}
            <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                    <IconUpload size={20} className="sm:w-6 sm:h-6 text-blue-400" />
                    <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-white">
                            Upload Entities
                        </h2>
                        <p className="text-gray-300 text-xs sm:text-sm">
                            Upload new entities to this collection
                        </p>
                    </div>
                </div>
                <EntityUploader DatabaseId={databaseId} setEntityCollection={setEntityCollection} fetchAllEntities={fetchAllEntities}/>
            </div>

            {/* Index Management Section */}
            {entityCollection && (
                <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <IconDatabase size={20} className="sm:w-6 sm:h-6 text-green-400" />
                        <div>
                            <h2 className="text-lg sm:text-xl font-semibold text-white">
                                Index Management
                            </h2>
                            <p className="text-gray-300 text-xs sm:text-sm">
                                Manage semantic and OASIS indexes for this collection
                            </p>
                        </div>
                    </div>
                    <IndexRequestManager databaseId={databaseId} entityCollectionId={entityCollection?.id || ""} />
                </div>
            )}

            {/* Entities List Section */}
            {paginatedEntities && paginatedEntities.results && paginatedEntities.results.length > 0 && (
                <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <IconList size={20} className="sm:w-6 sm:h-6 text-purple-400" />
                        <div>
                            <h2 className="text-lg sm:text-xl font-semibold text-white">
                                Entities
                            </h2>
                            <p className="text-gray-300 text-xs sm:text-sm">
                                View and manage entities in this collection ({paginatedEntities.count} total)
                            </p>
                        </div>
                    </div>
                    
                    {/* Pagination Controls - Top */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                            <label className="text-gray-300 text-sm">Page size:</label>
                            <select 
                                value={pageSize} 
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-white text-sm"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={!paginatedEntities.previous}
                                className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                                <IconChevronLeft size={16} />
                                Previous
                            </button>
                            <span className="text-gray-300 text-sm px-3 py-1">
                                Page {currentPage} of {Math.ceil(paginatedEntities.count / pageSize)}
                            </span>
                            <button
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={!paginatedEntities.next}
                                className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                                Next
                                <IconChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                    
                    <div className="text-gray-300 text-sm mb-4">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, paginatedEntities.count)} of {paginatedEntities.count} entities
                    </div>
                    
                    <div className="space-y-3 sm:space-y-4">
                        {paginatedEntities.results.map((entity: Entity) => (
                            <div key={entity.id} className="border border-gray-600 rounded-lg p-3 sm:p-4 bg-gray-700">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <h3 className="font-medium text-white text-sm sm:text-base">
                                            Entity - {entity.id}
                                        </h3>
                                        <span className="text-xs bg-blue-900 text-blue-200 px-2 py-1 rounded-full">
                                            ID: {entity.id}
                                        </span>
                                    </div>
                                    <AddQueryToEntity 
                                        entity={entity}
                                        entityCollectionId={entityCollection.id}
                                        buttonText="Add Query"
                                        buttonClassName="text-green-400 hover:text-green-300 bg-green-900/20 hover:bg-green-900/40 border border-green-700/50 hover:border-green-600/70 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
                                        />
                                </div>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <h4 className="font-medium text-gray-200 mb-2 text-sm sm:text-base">Content</h4>
                                        <div className="bg-gray-600 border border-gray-500 rounded p-2 sm:p-3 max-h-32 sm:max-h-40 overflow-y-auto">
                                            <pre className="text-xs text-gray-200 whitespace-pre-wrap">
                                                {JSON.stringify(entity.content, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h4 className="font-medium text-gray-200 mb-2 text-sm sm:text-base">Oasis Embedding</h4>
                                        <div className="bg-gray-600 border border-gray-500 rounded p-2 sm:p-3 max-h-32 sm:max-h-40 overflow-y-auto">
                                            <pre className="text-xs text-gray-200 whitespace-pre-wrap">
                                                {JSON.stringify(entity.embedding, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Pagination Controls - Bottom */}
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-gray-300 text-sm">
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, paginatedEntities.count)} of {paginatedEntities.count} entities
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={!paginatedEntities.previous}
                                className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                                <IconChevronLeft size={16} />
                                Previous
                            </button>
                            <span className="text-gray-300 text-sm px-3 py-1">
                                Page {currentPage} of {Math.ceil(paginatedEntities.count / pageSize)}
                            </span>
                            <button
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={!paginatedEntities.next}
                                className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                                Next
                                <IconChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Metadata Modal */}
            {showMetadataModal && (
                <MetadataModal 
                    metadata={{
                        entity_type: entityCollection.entity_type,
                        entity_description: entityCollection.entity_description,
                        all_keys: entityCollection.all_keys,
                        semantic_index_keys: entityCollection.semantic_index_keys,
                        oasis_index_keys: entityCollection.oasis_index_keys,
                        oasis_form_data_index_keys: entityCollection.oasis_form_data_index_keys || [],
                        ui_mapping: entityCollection.ui_mapping,
                        simple_keys: entityCollection.simple_keys
                    }} 
                    onClose={() => setShowMetadataModal(false)} 
                    onSave={saveMetadata}
                    saveButtonText="Save Metadata"
                />
            )}
        </div>
    )
}

export default EntityCollectionDetail;